import React from 'react';
import { Button } from '../../components/ui/button';
import { AlertTriangle, CheckCircle, Sparkles, ArrowRight } from 'lucide-react';
import { fmt } from './utils';

// ── Monthly Recap Content ──
const RecapContent = ({ recap, prevMonthLabel, onClose }) => {
  const { actualRev, actualExp, plannedRev, plannedExp, savingsRate, pctBesoins, pctEnvies, pctEpargne, ruleOk, overs, level, message } = recap;
  const clr = level === 'green' ? { bg: 'bg-emerald-500', text: 'text-emerald-400', light: 'bg-emerald-500/15', emoji: '\u{1F7E2}' }
    : level === 'yellow' ? { bg: 'bg-amber-500', text: 'text-amber-400', light: 'bg-amber-500/15', emoji: '\u{1F7E1}' }
    : { bg: 'bg-red-500', text: 'text-red-400', light: 'bg-red-500/15', emoji: '\u{1F534}' };

  const CompareRow = ({ label, planned, actual, invert }) => {
    const pct = planned > 0 ? (actual / planned) * 100 : 0;
    const isOver = invert ? actual > planned * 1.05 : actual < planned * 0.95;
    const cl = invert ? (actual <= planned ? 'text-emerald-400' : 'text-red-400') : (actual >= planned ? 'text-emerald-400' : 'text-amber-400');
    return (
      <div className="space-y-1.5">
        <div className="flex justify-between items-baseline">
          <span className="text-cap-text-secondary text-sm">{label}</span>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-cap-text-muted">{fmt(planned)}</span>
            <ArrowRight className="w-3 h-3 text-cap-text-muted" />
            <span className={`font-semibold ${cl}`}>{fmt(actual)}</span>
          </div>
        </div>
        <div className="relative h-2 bg-white/5 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${isOver ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
          {pct > 100 && <div className="absolute top-0 right-0 h-full bg-red-500/50 rounded-full" style={{ width: `${Math.min(pct - 100, 100)}%` }} />}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="recap-content">
      {/* Header banner */}
      <div className={`${clr.light} px-6 py-5`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-cap-text-muted text-xs font-medium uppercase tracking-wider mb-1">Bilan mensuel</p>
            <p className="text-white font-display text-lg font-bold capitalize">{prevMonthLabel}</p>
          </div>
          <div className={`p-3 rounded-2xl ${clr.light}`}>
            <Sparkles className={`w-6 h-6 ${clr.text}`} />
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Motivating message */}
        <div className={`p-4 rounded-xl ${clr.light} border border-white/5`} data-testid="recap-message">
          <p className={`${clr.text} font-medium text-sm leading-relaxed`}>{clr.emoji} {message}</p>
        </div>

        {/* Revenue vs Planned */}
        <CompareRow label="Revenus" planned={plannedRev} actual={actualRev} invert={false} />

        {/* Expenses vs Planned */}
        <CompareRow label="Dépenses" planned={plannedExp} actual={actualExp} invert={true} />

        {/* Savings rate */}
        <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl" data-testid="recap-savings">
          <span className="text-cap-text-secondary text-sm">Taux d'épargne</span>
          <span className={`font-display text-xl font-bold ${savingsRate >= 20 ? 'text-emerald-400' : savingsRate >= 10 ? 'text-amber-400' : 'text-red-400'}`}>
            {savingsRate.toFixed(1)}%
          </span>
        </div>

        {/* 50/30/20 */}
        <div data-testid="recap-rule">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white text-sm font-medium">Règle 50/30/20</span>
            {ruleOk
              ? <span className="flex items-center gap-1 text-emerald-400 text-xs font-medium"><CheckCircle className="w-3.5 h-3.5" /> Respectée</span>
              : <span className="flex items-center gap-1 text-amber-400 text-xs font-medium"><AlertTriangle className="w-3.5 h-3.5" /> À ajuster</span>
            }
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Besoins', pct: pctBesoins, target: 50, color: '#ef4444' },
              { label: 'Envies', pct: pctEnvies, target: 30, color: '#fbbf24' },
              { label: 'Épargne', pct: pctEpargne, target: 20, color: '#00e5a0' }
            ].map(r => {
              const isInv = r.label === 'Épargne';
              const ok = isInv ? r.pct >= r.target * 0.8 : r.pct <= r.target * 1.2;
              return (
                <div key={r.label} className="text-center p-2 bg-white/5 rounded-lg">
                  <p className="text-cap-text-muted text-[10px] mb-1">{r.label} ({isInv ? '\u2265' : '\u2264'}{r.target}%)</p>
                  <p className={`font-display text-base font-bold ${ok ? 'text-white' : 'text-red-400'}`}>{r.pct.toFixed(0)}%</p>
                  <div className="h-1 bg-white/10 rounded-full mt-1 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(r.pct / (r.target * 1.5) * 100, 100)}%`, backgroundColor: r.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Over-budget items */}
        {overs.length > 0 && (
          <div data-testid="recap-overs">
            <p className="text-white text-sm font-medium mb-2">Postes en dépassement</p>
            <div className="space-y-3">
              {overs.slice(0, 3).map((o, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 bg-red-500/10 rounded-lg">
                  <span className="text-cap-text-secondary text-sm">{o.name}</span>
                  <span className="text-red-400 text-sm font-semibold">+{fmt(o.over)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button onClick={onClose} className="w-full bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover" data-testid="recap-close">
          C'est noté !
        </Button>
      </div>
    </div>
  );
};

export default RecapContent;
